import axios from "axios";
import React, { useState, useEffect, useCallback } from "react";

const BASE_URL = process.env.REACT_APP_API_URL;

export default function AgentsPage() {
  const [activeType, setActiveType] = useState("agent");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [agentsData, setAgentsData] = useState([]);
  const [subAgentsData, setSubAgentsData] = useState([]);
  const [currentUserRole, setCurrentUserRole] = useState();
  const [activeTab, setActiveTab] = useState("agents");
  const itemsPerPage = 10;
  const [agents, setAgents] = useState([]);
  const [subagents, setSubagents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }, []);
  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("user"));
    if (currentUser) {
      setCurrentUserRole(currentUser.role);
    }
  }, []);

  const fetchWalletTransactions = useCallback(
    async (entityId) => {
      try {
        const response = await axios.get(
          `${BASE_URL}/wallet/transactions/${entityId}`,
          getAuthHeaders()
        );

        // Handle both possible response structures
        if (response.data && response.data.success) {
          return Array.isArray(response.data.data) ? response.data.data : [];
        }
        return Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error("Error fetching wallet transactions:", error);
        return [];
      }
    },
    [getAuthHeaders]
  );
  const fetchDocumentRecords = useCallback(
    async (userId) => {
      console.log("Inside the doc records");
      try {
        const response = await axios.get(
          `${BASE_URL}/services/get-agent-transactions?userId=${userId}`,
          getAuthHeaders()
        );

        console.log("Agent transactions:", response.data);

        // Handle both possible response structures
        if (response.data && response.data.success) {
          return Array.isArray(response.data.data) ? response.data.data : [];
        }
        // Fallback for different response structure
        return Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error("Error fetching document records:", error);
        return [];
      }
    },
    [getAuthHeaders]
  );

  const fetchWalletBalance = useCallback(
    async (entityId) => {
      try {
        const response = await axios.get(
          `${BASE_URL}/wallet/${entityId}`,
          getAuthHeaders()
        );

        // Properly access transactions from walletDetails
        const transactions = response.data?.walletDetails?.transactions || [];

        return {
          balance: response.data.balance || 0,
          transactions: transactions,
        };
      } catch (error) {
        console.error("Error fetching wallet balance:", error);
        return {
          balance: 0,
          transactions: [],
        };
      }
    },
    [getAuthHeaders]
  );

  const isUserActive = useCallback((transactions) => {
    if (!transactions || !transactions.length) return false;

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    return transactions.some((txn) => {
      try {
        const txnDate = new Date(txn.createdAt);
        return txnDate >= sevenDaysAgo;
      } catch (e) {
        console.error("Error parsing transaction date:", e);
        return false;
      }
    });
  }, []);

  // Update processAgentsData to properly count document downloads
  const processAgentsData = useCallback(
    async (users, isSubagent = false) => {
      setLoading(true);
      try {
        if (!users || !Array.isArray(users)) {
          console.error("Invalid users data:", users);
          return [];
        }

        let agentsMap = {};
        if (isSubagent) {
          try {
            const agentsResponse = await axios.get(
              `${BASE_URL}/users/get-all-agents`,
              getAuthHeaders()
            );

            const agentsList = Array.isArray(agentsResponse.data?.data)
              ? agentsResponse.data.data
              : Array.isArray(agentsResponse.data)
              ? agentsResponse.data
              : [];

            agentsList.forEach((agent) => {
              agentsMap[agent._id] = agent.name;
            });
          } catch (error) {
            console.error("Error fetching agents for mapping:", error);
          }
        }

        const processedData = await Promise.all(
          users.map(async (user, index) => {
            try {
              const walletData = await fetchWalletBalance(
                user.entityId || user._id
              );

              // Count successful document downloads (debit transactions with document references)
              const documentsDownloaded = walletData.transactions.filter(
                (txn) =>
                  txn.type === "debit" &&
                  txn.deductedFor &&
                  ["driving-license", "pan", "rc", "voter-id"].includes(
                    txn.deductedFor
                  )
              ).length;

              const result = {
                slNo: index + 1,
                id: user._id,
                joinedDate: new Date(user.createdAt).toLocaleDateString(),
                name: user.name || "N/A",
                phone: user.phone || "N/A",
                walletBalance: walletData.balance || 0,
                documentsDownloaded,
                status: isUserActive(walletData.transactions)
                  ? "Active"
                  : "Inactive",
                entityId: user.entityId || user._id,
                parentAgent: isSubagent
                  ? agentsMap[user.parentId] || "N/A"
                  : "N/A",
              };

              return result;
            } catch (error) {
              console.error(`Error processing user ${user._id}:`, error);
              return null;
            }
          })
        );

        const filteredData = processedData.filter((item) => item !== null);
        return filteredData;
      } catch (error) {
        console.error("Error processing agent data:", error);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [fetchWalletBalance, isUserActive, getAuthHeaders]
  );

  const fetchAllAgents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/users/get-all-agents`,
        getAuthHeaders()
      );

      const agentsList = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
        ? response.data
        : [];

      const processedData = await processAgentsData(agentsList);
      setAgentsData(processedData);
      setAgents(agentsList);
    } catch (error) {
      console.error("Failed to fetch agents:", error);
      setAgentsData([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, processAgentsData]);

  const fetchAllSubagents = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${BASE_URL}/users/get-all-sub-agents`,
        getAuthHeaders()
      );

      const subagentsList = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
        ? response.data
        : [];

      const processedData = await processAgentsData(subagentsList, true);

      setSubAgentsData(processedData);
      setSubagents(subagentsList);
    } catch (error) {
      console.error("Failed to fetch subagents:", error);
      setSubAgentsData([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, processAgentsData]);

  const fetchSubagentsForAgent = useCallback(
    async (isAgentUser = false) => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${BASE_URL}/users/get-all-sub-agents`,
          getAuthHeaders()
        );

        // Ensure we're working with an array
        const allSubagents = Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
          ? response.data
          : [];

        // For agent users, only show their own subagents
        let filteredSubagents = allSubagents;
        if (isAgentUser) {
          const currentUserId = JSON.parse(localStorage.getItem("user"))?._id;
          filteredSubagents = allSubagents.filter(
            (subagent) => subagent.parentId === currentUserId
          );
        }

        const processedData = await processAgentsData(filteredSubagents, true);
        setSubAgentsData(processedData);
        setSubagents(filteredSubagents);
      } catch (error) {
        console.error("Failed to fetch subagents:", error);
        setSubAgentsData([]);
      } finally {
        setLoading(false);
      }
    },
    [getAuthHeaders, processAgentsData]
  );
  const handleAgentSelect = useCallback(
    (e) => {
      if (currentUserRole !== "admin") return; // Only allow selection for admin
      setSelectedAgentId(e.target.value);
      fetchSubagentsForAgent(); // Just fetch all subagents
    },
    [fetchSubagentsForAgent, currentUserRole]
  );
  const handleTypeChange = (type) => {
    setActiveType(type);
    setCurrentPage(1);

    if (type === "subagent") {
      if (currentUserRole === "admin") {
        fetchAllSubagents();
      } else if (currentUserRole === "agent") {
        fetchSubagentsForAgent(true);
      }
    }
  };
  useEffect(() => {
    if (!currentUserRole) {
      return;
    }

    if (currentUserRole === "admin") {
      fetchAllAgents();
      if (activeTab === "subagents") {
        fetchAllSubagents(); // Remove the null parameter
      }
    } else if (currentUserRole === "agent") {
      setActiveTab("subagents");
      fetchSubagentsForAgent(true);
    }
  }, [
    activeTab,
    currentUserRole,
    fetchAllAgents,
    fetchAllSubagents,
    fetchSubagentsForAgent,
  ]);

  useEffect(() => {
    const data = activeType === "agent" ? agentsData : subAgentsData;
    const activeCount = data.filter((item) => item.status === "Active").length;

    setStats({
      total: data.length,
      active: activeCount,
      inactive: data.length - activeCount,
    });
  }, [activeType, agentsData, subAgentsData]);

  const filteredData = React.useMemo(() => {
    const data = activeType === "agent" ? agentsData : subAgentsData;

    const result = search
      ? data.filter((entry) =>
          entry.name.toLowerCase().includes(search.toLowerCase())
        )
      : data;

    return result;
  }, [activeType, agentsData, subAgentsData, search]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [search, activeType]);

  useEffect(() => {}, [agentsData, subAgentsData]);
  return (
    <div className="p-6 md:p-10 bg-gray-50 min-h-screen">
      <h1 className="text-2xl font-bold mb-2">Agents & Subagents</h1>
      <p className="text-md mb-6">Activity in last 7 days</p>

      <div className="flex gap-4 mb-6">
        <button
          onClick={() => handleTypeChange("agent")}
          className={`px-4 py-2 rounded-md font-medium ${
            activeType === "agent"
              ? "bg-purple-600 text-white"
              : "bg-white border"
          }`}
        >
          Agents
        </button>
        <button
          onClick={() => handleTypeChange("subagent")}
          className={`px-4 py-2 rounded-md font-medium ${
            activeType === "subagent"
              ? "bg-cyan-600 text-white"
              : "bg-white border"
          }`}
        >
          Subagents
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white shadow rounded-xl p-4 flex items-center gap-3">
          <div className="w-4 h-4 bg-purple-500 rounded-full"></div>
          <div>
            <p className="text-sm">
              Total {activeType === "agent" ? "Agents" : "Subagents"}
            </p>
            <p className="text-xl font-semibold">{stats.total}</p>
          </div>
        </div>
        <div className="bg-white shadow rounded-xl p-4 flex items-center gap-3">
          <div className="w-4 h-4 bg-cyan-400 rounded-full"></div>
          <div>
            <p className="text-sm">Active</p>
            <p className="text-xl font-semibold">{stats.active}</p>
          </div>
        </div>
        <div className="bg-white shadow rounded-xl p-4 flex items-center gap-3">
          <div className="w-4 h-4 bg-red-400 rounded-full"></div>
          <div>
            <p className="text-sm">Inactive</p>
            <p className="text-xl font-semibold">{stats.inactive}</p>
          </div>
        </div>
      </div>

      {currentUserRole === "admin" && activeTab === "subagents" && (
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Select Agent</label>
          <select
            onChange={handleAgentSelect}
            value={selectedAgentId || ""}
            className="border border-gray-300 rounded-md px-3 py-2 w-full max-w-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">All Agents</option>
            {agents.map((agent) => (
              <option key={agent._id} value={agent._id}>
                {agent.name} ({agent.phone})
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="bg-white shadow rounded-xl p-4">
        <div className="mb-4 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <h2 className="text-lg font-semibold">
            {activeType === "agent" ? "Agents List" : "Subagents List"}
          </h2>
          <div className="w-full md:w-auto">
            <input
              type="text"
              placeholder="Search by name"
              className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        {currentUserRole === "admin" && activeTab === "subagents" && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Select Agent
            </label>
            <select
              onChange={handleAgentSelect}
              value={selectedAgentId || ""}
              className="border border-gray-300 rounded-md px-3 py-2 w-full max-w-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">All Agents</option>
              {agents.map((agent) => (
                <option key={agent._id} value={agent._id}>
                  {agent.name} ({agent.phone})
                </option>
              ))}
            </select>
          </div>
        )}
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
            <p className="mt-2">Loading data...</p>
          </div>
        ) : (
          <>
            <div className="overflow-auto">
              <table className="min-w-full text-sm text-left">
                <thead className="bg-gray-100 font-semibold">
                  <tr>
                    <th className="p-3">Sl No</th>
                    <th className="p-3">Joined Date</th>
                    <th className="p-3">Name</th>
                    <th className="p-3">Phone</th>
                    {activeType === "subagent" && (
                      <th className="p-3">Parent Agent</th>
                    )}
                    <th className="p-3">Wallet Balance</th>
                    <th className="p-3">Downloads</th>
                    <th className="p-3">Status</th>
                    {/* <th className="p-3">Action</th> */}
                  </tr>
                </thead>
                <tbody>
                  {currentData.length > 0 ? (
                    currentData.map((entry) => (
                      <tr key={entry.id} className="border-b hover:bg-gray-50">
                        <td className="p-3">{entry.slNo}</td>
                        <td className="p-3">{entry.joinedDate}</td>
                        <td className="p-3 font-medium">{entry.name}</td>
                        <td className="p-3">{entry.phone}</td>
                        {activeType === "subagent" && (
                          <td className="p-3">{entry.parentAgent}</td>
                        )}
                        <td className="p-3">
                          ₹{entry.walletBalance.toLocaleString()}
                        </td>
                        <td className="p-3">{entry.documentsDownloaded}</td>
                        <td className="p-3">
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              entry.status === "Active"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {entry.status}
                          </span>
                        </td>
                        {/* <td className="p-3">
                          <button
                            className="px-3 py-1 border rounded-md hover:bg-gray-100"
                            onClick={() =>
                              console.log("View details", entry.id)
                            }
                          >
                            ...
                          </button>
                        </td> */}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="8"
                        className="text-center py-6 text-gray-500"
                      >
                        {loading ? "Loading..." : "No records found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {filteredData.length > 0 && (
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center px-4 py-3 text-sm gap-3 border-t">
                <div>
                  Showing {startIndex + 1} to{" "}
                  {Math.min(startIndex + itemsPerPage, filteredData.length)} of{" "}
                  {filteredData.length} entries
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border rounded-md disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>

                  <div className="flex gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-1 border rounded-md ${
                            currentPage === page
                              ? "bg-purple-500 text-white"
                              : "hover:bg-gray-50"
                          }`}
                        >
                          {page}
                        </button>
                      )
                    )}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border rounded-md disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
